import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { paged } from "./AdminPagedList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Ticket, Plus, Edit2, Trash2, ChevronDown, ChevronUp, 
  DollarSign, Calendar, Package, Check, X, Copy, Tag
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  try {
    return new Date(isoString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return isoString;
  }
};

const describeLotConditions = (lot) => {
  const parts = [];

  if (lot.ends_at) {
    parts.push(`Fim da venda: ${formatDateTime(lot.ends_at)}`);
  } else if (lot.end_before_value && lot.end_before_unit && lot.end_before_reference) {
    const unitLabels = {
      minutes: "min",
      hours: "h",
      days: "dias",
    };
    const referenceLabels = {
      event_start: "início do evento",
      event_end: "fim do evento",
    };
    parts.push(
      `Fim da venda: ${lot.end_before_value} ${unitLabels[lot.end_before_unit] || lot.end_before_unit} antes de ${referenceLabels[lot.end_before_reference] || lot.end_before_reference}`
    );
  }

  if (lot.start_after_lot) {
    parts.push(`Virada: após ${lot.start_after_lot} esgotar`);
  }

  return parts.join(" • ");
};

const DEFAULT_GLOBAL_FEES = {
  percentage: 5,
  fixed: 0.40,
};

const createEmptyQuantityPricingRule = (minQuantity = 2) => ({
  min_quantity: minQuantity,
  rule_type: "percent_discount",
  value: 10,
  label: "",
});

const normalizeQuantityPricingRules = (rules = []) => {
  return [...(rules || [])]
    .map((rule, index) => ({
      min_quantity: Math.max(1, parseInt(rule?.min_quantity, 10) || index + 1),
      rule_type: rule?.rule_type === "fixed_discount"
        ? "fixed_discount"
        : rule?.rule_type === "fixed_price"
          ? "fixed_price"
          : "percent_discount",
      value: Math.max(0, Number(rule?.value) || 0),
      label: rule?.label || "",
    }))
    .sort((a, b) => a.min_quantity - b.min_quantity);
};

const describeQuantityPricingRule = (rule) => {
  if (!rule) return "";
  if (rule.rule_type === "fixed_discount") {
    return `${rule.min_quantity}+ ingressos: -${formatCurrency(rule.value)} por ingresso`;
  }
  if (rule.rule_type === "fixed_price") {
    return `${rule.min_quantity}+ ingressos por ${formatCurrency(rule.value)} cada`;
  }
  return `${rule.min_quantity}+ ingressos: ${Number(rule.value || 0).toFixed(0)}% OFF`;
};

const calculateQuantityPricingPreview = (basePrice, quantity, rules = []) => {
  const normalizedBasePrice = Math.max(0, Number(basePrice) || 0);
  const normalizedQuantity = Math.max(1, parseInt(quantity, 10) || 1);
  const normalizedRules = normalizeQuantityPricingRules(rules);

  let appliedRule = null;
  let unitPrice = normalizedBasePrice;

  normalizedRules.forEach((rule) => {
    if (normalizedQuantity < rule.min_quantity) return;
    const candidatePrice = rule.rule_type === "fixed_price"
      ? Math.max(0, Number(rule.value) || 0)
      : rule.rule_type === "fixed_discount"
        ? Math.max(0, normalizedBasePrice - (Number(rule.value) || 0))
        : Math.max(0, normalizedBasePrice - (normalizedBasePrice * (Number(rule.value) || 0) / 100));
    if (!appliedRule || candidatePrice < unitPrice) {
      appliedRule = rule;
      unitPrice = candidatePrice;
    }
  });

  const subtotal = unitPrice * normalizedQuantity;
  const baseSubtotal = normalizedBasePrice * normalizedQuantity;

  return {
    baseUnitPrice: normalizedBasePrice,
    unitPrice,
    quantity: normalizedQuantity,
    subtotal,
    savings: Math.max(0, baseSubtotal - subtotal),
    appliedRule,
  };
};

const createEmptyForm = (globalFees = DEFAULT_GLOBAL_FEES) => ({
  name: "",
  description: "",
  base_price: 0,
  use_custom_fee: false,
  fee_percentage: globalFees.percentage,
  fee_fixed: globalFees.fixed,
  min_qty: 1,
  max_qty: null,
  capacity: null,
  is_active: true,
  visibility: "available",
  payment_provider: "auto",
  lots: [],
  quantity_pricing: [],
});

export function TicketCatalogManager({ readOnly = false, embedded = false, onClose = null, onLocalCreate = null }) {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(embedded); // Se embedded, já abre em modo criação
  const [editingItem, setEditingItem] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [saveForFuture, setSaveForFuture] = useState(true); // Only used in embedded mode
  const [globalFees, setGlobalFees] = useState(DEFAULT_GLOBAL_FEES);
  const [savingGlobalFees, setSavingGlobalFees] = useState(false);
  const [quantityPreview, setQuantityPreview] = useState(1);
  
  // Form state
  const [form, setForm] = useState(createEmptyForm(DEFAULT_GLOBAL_FEES));
  const quantityPricingPreview = calculateQuantityPricingPreview(form.base_price, quantityPreview, form.quantity_pricing);

  const fetchTicketTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ticket-catalog?include_inactive=true`, { headers: getAuthHeaders() });
      setTicketTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching ticket catalog:", error);
      toast.error("Erro ao carregar catálogo de ingressos");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGlobalFees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cms/settings`);
      const ticketFees = response.data?.ticketFees || {};
      setGlobalFees({
        percentage: Number.isFinite(Number(ticketFees.percentage)) ? Number(ticketFees.percentage) : DEFAULT_GLOBAL_FEES.percentage,
        fixed: Number.isFinite(Number(ticketFees.fixed)) ? Number(ticketFees.fixed) : DEFAULT_GLOBAL_FEES.fixed,
      });
    } catch (error) {
      console.error("Error fetching global ticket fees:", error);
      setGlobalFees(DEFAULT_GLOBAL_FEES);
    }
  }, []);

  useEffect(() => {
    fetchTicketTypes();
    fetchGlobalFees();
  }, [fetchTicketTypes, fetchGlobalFees]);

  useEffect(() => {
    setForm((prev) => (
      prev.use_custom_fee
        ? prev
        : {
            ...prev,
            fee_percentage: globalFees.percentage,
            fee_fixed: globalFees.fixed,
          }
    ));
  }, [globalFees]);

  const resetForm = () => {
    setForm(createEmptyForm(globalFees));
    setEditingItem(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    const useCustomFee = item.use_custom_fee === true;
    setForm({
      name: item.name || "",
      description: item.description || "",
      base_price: item.base_price || 0,
      use_custom_fee: useCustomFee,
      fee_percentage: useCustomFee ? (item.fee_percentage ?? globalFees.percentage) : globalFees.percentage,
      fee_fixed: useCustomFee ? (item.fee_fixed ?? globalFees.fixed) : globalFees.fixed,
      min_qty: item.min_qty || 1,
      max_qty: item.max_qty || null,
      capacity: item.capacity || null,
      is_active: item.is_active !== false,
      visibility: item.visibility || "available",
      payment_provider: item.payment_provider || "auto",
      lots: item.lots || [],
      quantity_pricing: normalizeQuantityPricingRules(item.quantity_pricing || []),
    });
    setShowModal(true);
  };

  const buildLotsWithEffectiveFees = (formData) => {
    const effectivePercentage = formData.use_custom_fee ? formData.fee_percentage : globalFees.percentage;
    const effectiveFixed = formData.use_custom_fee ? formData.fee_fixed : globalFees.fixed;
    return (formData.lots || []).map((lot) => ({
      ...lot,
      fee_percentage: effectivePercentage,
      fee_fixed: effectiveFixed,
    }));
  };

  const saveGlobalFeeSettings = async () => {
    try {
      setSavingGlobalFees(true);
      await axios.put(
        `${API}/cms/settings`,
        { ticketFees: { percentage: globalFees.percentage, fixed: globalFees.fixed } },
        { headers: getAuthHeaders() }
      );
      toast.success("Taxa padrão salva");
      await fetchGlobalFees();
    } catch (error) {
      console.error("Error saving global fees:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar taxa padrão");
    } finally {
      setSavingGlobalFees(false);
    }
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error("Nome é obrigatório");
      return;
    }

    const payload = {
      ...form,
      lots: buildLotsWithEffectiveFees(form),
      quantity_pricing: normalizeQuantityPricingRules(form.quantity_pricing),
      fee_percentage: form.use_custom_fee ? form.fee_percentage : globalFees.percentage,
      fee_fixed: form.use_custom_fee ? form.fee_fixed : globalFees.fixed,
    };

    // Embedded mode: "only for this event" (not saved to catalog)
    if (embedded && !saveForFuture && onLocalCreate) {
      onLocalCreate(payload);
      toast.success("Ingresso adicionado ao evento");
      if (onClose) onClose();
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/ticket-catalog/${editingItem.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Tipo de ingresso atualizado");
      } else {
        const res = await axios.post(`${API}/ticket-catalog`, payload, { headers: getAuthHeaders() });
        toast.success("Tipo de ingresso criado");
        // In embedded mode with saveForFuture, also select it for the event
        if (embedded && onLocalCreate && res.data?.id) {
          onLocalCreate({ ...payload, catalogId: res.data.id });
        }
      }
      setShowModal(false);
      resetForm();
      fetchTicketTypes();
      
      // Se em modo embedded, fechar o modal parent
      if (embedded && onClose) {
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Tem certeza que deseja remover este tipo de ingresso?")) return;
    
    try {
      await axios.delete(`${API}/ticket-catalog/${id}`, { headers: getAuthHeaders() });
      toast.success("Tipo de ingresso removido");
      fetchTicketTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao remover");
    }
  };

  const handleDuplicate = (item) => {
    const useCustomFee = item.use_custom_fee === true;
    setEditingItem(null);
    setForm({
      name: `${item.name} (cópia)`,
      description: item.description || "",
      base_price: item.base_price || 0,
      use_custom_fee: useCustomFee,
      fee_percentage: useCustomFee ? (item.fee_percentage ?? globalFees.percentage) : globalFees.percentage,
      fee_fixed: useCustomFee ? (item.fee_fixed ?? globalFees.fixed) : globalFees.fixed,
      min_qty: item.min_qty || 1,
      max_qty: item.max_qty || null,
      capacity: item.capacity || null,
      is_active: true,
      visibility: item.visibility || "available",
      payment_provider: item.payment_provider || "auto",
      lots: (item.lots || []).map(lot => ({ ...lot, id: null, sold_count: 0 })),
      quantity_pricing: normalizeQuantityPricingRules(item.quantity_pricing || []),
    });
    setShowModal(true);
  };

  // Lot management within form
  const addLot = () => {
    const newLot = {
      id: null,
      name: `${form.lots.length + 1}º Lote`,
      price: form.base_price,
      fee_percentage: form.fee_percentage,
      fee_fixed: form.fee_fixed,
      starts_at: "",
      ends_at: "",
      quantity_limit: null,
      is_active: true,
      visibility: "available" // "available" | "always" | "hidden"
    };
    setForm(prev => ({ ...prev, lots: [...prev.lots, newLot] }));
  };

  const updateLot = (index, field, value) => {
    setForm(prev => {
      const lots = [...prev.lots];
      lots[index] = { ...lots[index], [field]: value };
      return { ...prev, lots };
    });
  };

  const removeLot = (index) => {
    setForm(prev => ({
      ...prev,
      lots: prev.lots.filter((_, i) => i !== index)
    }));
  };

  const moveLot = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= form.lots.length) return;
    
    setForm(prev => {
      const lots = [...prev.lots];
      [lots[index], lots[newIndex]] = [lots[newIndex], lots[index]];
      return { ...prev, lots };
    });
  };

  const addQuantityPricingRule = () => {
    const lastMinQuantity = form.quantity_pricing?.length
      ? Math.max(...form.quantity_pricing.map((rule) => Number(rule.min_quantity) || 1))
      : 1;
    setForm((prev) => ({
      ...prev,
      quantity_pricing: [
        ...(prev.quantity_pricing || []),
        createEmptyQuantityPricingRule(lastMinQuantity + 1),
      ],
    }));
  };

  const updateQuantityPricingRule = (index, field, value) => {
    setForm((prev) => {
      const nextRules = [...(prev.quantity_pricing || [])];
      nextRules[index] = {
        ...nextRules[index],
        [field]: value,
      };
      return {
        ...prev,
        quantity_pricing: normalizeQuantityPricingRules(nextRules),
      };
    });
  };

  const removeQuantityPricingRule = (index) => {
    setForm((prev) => ({
      ...prev,
      quantity_pricing: (prev.quantity_pricing || []).filter((_, ruleIndex) => ruleIndex !== index),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#fccf1f]"></div>
      </div>
    );
  }

  // Renderiza o formulário de criação inline (para modo embedded)
  const renderForm = () => (
    <div className="space-y-4">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label className="text-zinc-400">Nome *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="Ex: Ingresso Individual"
            data-testid="input-name"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-zinc-400">Descrição</Label>
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="Descrição opcional"
          />
        </div>
      </div>

      {/* Pricing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-400">Preço Base (R$)</Label>
          <Input
            type="number"
            step="0.01"
            value={form.base_price}
            onChange={(e) => setForm({ ...form, base_price: parseFloat(e.target.value) || 0 })}
            className="bg-zinc-800 border-zinc-700 text-white"
            data-testid="input-price"
          />
        </div>
        <div className="md:col-span-2 space-y-3 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              onClick={() => setForm((prev) => ({
                ...prev,
                use_custom_fee: !prev.use_custom_fee,
                fee_percentage: !prev.use_custom_fee ? prev.fee_percentage : globalFees.percentage,
                fee_fixed: !prev.use_custom_fee ? prev.fee_fixed : globalFees.fixed,
              }))}
              className={`w-10 h-6 rounded-full transition-colors ${form.use_custom_fee ? "bg-[#fccf1f]" : "bg-zinc-700"}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${form.use_custom_fee ? "translate-x-4" : ""}`} />
            </button>
            <div>
              <p className="text-zinc-200 text-sm font-medium">Taxa Personalizada</p>
              <p className="text-zinc-500 text-xs">Desmarcado: usa a taxa padrão da Aba Ingressos</p>
            </div>
          </label>
          {form.use_custom_fee ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Taxa (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.fee_percentage}
                  onChange={(e) => setForm({ ...form, fee_percentage: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Taxa Fixa (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.fee_fixed}
                  onChange={(e) => setForm({ ...form, fee_fixed: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-300">
              Taxa padrão ativa: <span className="font-medium text-white">{globalFees.percentage}% + {formatCurrency(globalFees.fixed)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Quantity & Capacity */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <Label className="text-zinc-300">Provedor de pagamento</Label>
        <select
          value={form.payment_provider}
          onChange={(e) => setForm({ ...form, payment_provider: e.target.value })}
          className="mt-2 h-10 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 text-sm text-white"
          data-testid="ticket-payment-provider"
        >
          <option value="auto">Automático: Asaas no presencial e InfinitePay no digital</option>
          <option value="infinitepay">InfinitePay</option>
          <option value="asaas">Asaas</option>
        </select>
        <p className="mt-2 text-xs text-zinc-500">
          Segurança: o Asaas só será aceito se o total final, já com descontos, for de pelo menos R$ 5,00.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-400">Qtd. Mínima</Label>
          <Input
            type="number"
            value={form.min_qty}
            onChange={(e) => setForm({ ...form, min_qty: parseInt(e.target.value) || 1 })}
            className="bg-zinc-800 border-zinc-700 text-white"
          />
        </div>
        <div>
          <Label className="text-zinc-400">Qtd. Máxima</Label>
          <Input
            type="number"
            value={form.max_qty || ""}
            onChange={(e) => setForm({ ...form, max_qty: e.target.value ? parseInt(e.target.value) : null })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="Ilimitado"
          />
        </div>
        <div>
          <Label className="text-zinc-400">Capacidade Total</Label>
          <Input
            type="number"
            value={form.capacity || ""}
            onChange={(e) => setForm({ ...form, capacity: e.target.value ? parseInt(e.target.value) : null })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="Ilimitada"
          />
        </div>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setForm({ ...form, is_active: !form.is_active })}
          className={`w-10 h-6 rounded-full transition-colors ${form.is_active ? "bg-green-600" : "bg-zinc-700"}`}
        >
          <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${form.is_active ? "translate-x-4" : ""}`} />
        </button>
        <span className="text-zinc-400 text-sm">Ativo</span>
      </div>

      {/* Visibility */}
      <div>
        <Label className="text-zinc-400 mb-2 block">Visibilidade do Ingresso</Label>
        <div className="flex gap-2 flex-wrap">
          {[
            { value: "available", label: "Visível quando disponível", icon: "👁️" },
            { value: "always", label: "Sempre visível", icon: "✨" },
            { value: "hidden", label: "Oculto", icon: "🔒" },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setForm({ ...form, visibility: opt.value })}
              className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                form.visibility === opt.value
                  ? "bg-[#fccf1f] text-black"
                  : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
              }`}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
        {form.visibility === "hidden" && (
          <p className="text-amber-400/80 text-xs mt-2 bg-amber-500/10 p-2 rounded">
            ⚠️ Ingressos ocultos só ficam visíveis quando um cupom específico é aplicado.
          </p>
        )}
      </div>

      {/* Quantity Pricing Section */}
      <div className="border-t border-zinc-800 pt-4">
        <div className="flex justify-between items-center mb-3">
          <Label className="text-zinc-400 flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Desconto Progressivo por Quantidade ({form.quantity_pricing.length})
          </Label>
          <Button
            variant="outline"
            size="sm"
            onClick={addQuantityPricingRule}
            className="border-zinc-700 text-zinc-400"
          >
            <Plus className="w-3 h-3 mr-1" />
            Adicionar Faixa
          </Button>
        </div>

        {form.quantity_pricing.length === 0 ? (
          <p className="text-zinc-500 text-sm bg-zinc-800/50 p-4 rounded text-center">
            Sem desconto progressivo configurado. O lote vigente define o preço base e as faixas entram por quantidade.
          </p>
        ) : (
          <div className="space-y-3 mb-4">
            <div className="rounded-xl border border-purple-500/20 bg-purple-500/10 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-white font-semibold">Simulador de Oferta</p>
                  <p className="text-zinc-400 text-sm">
                    Veja exatamente qual faixa será aplicada para a quantidade escolhida.
                  </p>
                </div>
                <div className="w-full md:w-40">
                  <Label className="text-zinc-400 text-xs">Quantidade simulada</Label>
                  <Input
                    type="number"
                    min="1"
                    value={quantityPreview}
                    onChange={(e) => setQuantityPreview(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="bg-zinc-900 border-zinc-700 text-white"
                  />
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-4 mt-4">
                <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                  <p className="text-zinc-500 text-xs">Preço base por ingresso</p>
                  <p className="text-white font-semibold">{formatCurrency(quantityPricingPreview.baseUnitPrice)}</p>
                </div>
                <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                  <p className="text-zinc-500 text-xs">Preço aplicado</p>
                  <p className="text-white font-semibold">{formatCurrency(quantityPricingPreview.unitPrice)}</p>
                </div>
                <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                  <p className="text-zinc-500 text-xs">Economia nesta quantidade</p>
                  <p className="text-emerald-400 font-semibold">{formatCurrency(quantityPricingPreview.savings)}</p>
                </div>
                <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                  <p className="text-zinc-500 text-xs">Faixa aplicada</p>
                  <p className="text-white font-semibold text-sm">
                    {quantityPricingPreview.appliedRule
                      ? (quantityPricingPreview.appliedRule.label || describeQuantityPricingRule(quantityPricingPreview.appliedRule))
                      : "Preço base do lote"}
                  </p>
                </div>
              </div>
            </div>
            {form.quantity_pricing.map((rule, idx) => (
              <div key={`quantity-rule-${idx}`} className="bg-zinc-800/50 p-4 rounded-xl space-y-3 border border-white/5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-medium">{describeQuantityPricingRule(rule)}</p>
                      {idx === form.quantity_pricing.length - 1 && (
                        <span className="text-[10px] uppercase tracking-wide bg-[#fccf1f]/20 text-[#fccf1f] px-2 py-1 rounded-full">
                          Melhor oferta
                        </span>
                      )}
                    </div>
                    <p className="text-zinc-500 text-xs">
                      A melhor faixa válida será aplicada sobre o lote vigente no checkout.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeQuantityPricingRule(idx)}
                    className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-zinc-500 text-xs">Qtd. Mínima</Label>
                    <Input
                      type="number"
                      min="1"
                      value={rule.min_quantity}
                      onChange={(e) => updateQuantityPricingRule(idx, "min_quantity", parseInt(e.target.value, 10) || 1)}
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Tipo da Regra</Label>
                    <select
                      value={rule.rule_type}
                      onChange={(e) => updateQuantityPricingRule(idx, "rule_type", e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 h-10"
                    >
                      <option value="percent_discount">% OFF sobre o lote</option>
                      <option value="fixed_discount">Desconto fixo (R$)</option>
                      <option value="fixed_price">Preço final fixo (legado)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">
                      {rule.rule_type === "fixed_price"
                        ? "Preço Final (R$)"
                        : rule.rule_type === "fixed_discount"
                          ? "Desconto (R$)"
                          : "Desconto (%)"}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step={rule.rule_type === "percent_discount" ? "0.1" : "0.01"}
                      value={rule.value}
                      onChange={(e) => updateQuantityPricingRule(idx, "value", parseFloat(e.target.value) || 0)}
                      className="bg-zinc-900 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Rótulo Opcional</Label>
                    <Input
                      value={rule.label || ""}
                      onChange={(e) => updateQuantityPricingRule(idx, "label", e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white"
                      placeholder="Ex: Leve 3 e pague menos"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

            {/* Lots Section */}
            <div className="border-t border-zinc-800 pt-4">
        <div className="flex justify-between items-center mb-3">
          <Label className="text-zinc-400 flex items-center gap-2">
            <Package className="w-4 h-4" />
            Lotes ({form.lots.length})
          </Label>
          <Button 
            type="button"
            variant="outline" 
            size="sm" 
            onClick={addLot}
            className="border-zinc-700 text-zinc-400"
            data-testid="add-lot-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Adicionar Lote
          </Button>
        </div>

        {form.lots.length === 0 ? (
          <p className="text-zinc-500 text-sm bg-zinc-800/50 p-4 rounded text-center">
            Sem lotes configurados. O preço base será usado.
          </p>
        ) : (
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {form.lots.map((lot, idx) => (
              <div key={lot.id || idx} className="bg-zinc-800/50 p-3 rounded space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-1">
                      <button 
                        type="button"
                        onClick={() => moveLot(idx, -1)} 
                        disabled={idx === 0}
                        className="text-zinc-500 hover:text-white disabled:opacity-30"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button 
                        type="button"
                        onClick={() => moveLot(idx, 1)} 
                        disabled={idx === form.lots.length - 1}
                        className="text-zinc-500 hover:text-white disabled:opacity-30"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="text-zinc-400 text-sm font-medium">Lote {idx + 1}</span>
                  </div>
                  <Button 
                    type="button"
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeLot(idx)}
                    className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {describeLotConditions(lot) && (
                  <p className="text-[11px] text-zinc-400 rounded-md bg-zinc-900/60 border border-zinc-700/70 px-2.5 py-2">
                    {describeLotConditions(lot)}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-zinc-500 text-xs">Nome</Label>
                    <Input
                      value={lot.name}
                      onChange={(e) => updateLot(idx, "name", e.target.value)}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                      placeholder="1º Lote"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Preço (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={lot.price}
                      onChange={(e) => updateLot(idx, "price", parseFloat(e.target.value) || 0)}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Início (Data)</Label>
                    <Input
                      type="datetime-local"
                      value={lot.starts_at ? lot.starts_at.slice(0, 16) : ""}
                      onChange={(e) => updateLot(idx, "starts_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Fim (Data)</Label>
                    <Input
                      type="datetime-local"
                      value={lot.ends_at ? lot.ends_at.slice(0, 16) : ""}
                      onChange={(e) => updateLot(idx, "ends_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Limite de Quantidade</Label>
                    <Input
                      type="number"
                      value={lot.quantity_limit || ""}
                      onChange={(e) => updateLot(idx, "quantity_limit", e.target.value ? parseInt(e.target.value) : null)}
                      className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                      placeholder="Ilimitado"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => updateLot(idx, "is_active", !lot.is_active)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                        lot.is_active ? "bg-green-600/20 text-green-400" : "bg-zinc-700 text-zinc-400"
                      }`}
                    >
                      {lot.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {lot.is_active ? "Ativo" : "Inativo"}
                    </button>
                  </div>
                </div>
                
                {/* Lot Visibility */}
                <div className="mt-2">
                  <Label className="text-zinc-500 text-xs mb-1 block">Visibilidade do Lote</Label>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { value: "available", label: "Disponível" },
                      { value: "always", label: "Sempre" },
                      { value: "hidden", label: "Oculto 🔒" },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateLot(idx, "visibility", opt.value)}
                        className={`px-2 py-1 rounded text-xs transition-colors ${
                          (lot.visibility || "available") === opt.value
                            ? "bg-[#fccf1f] text-black"
                            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Advanced Options */}
                <div className="border-t border-zinc-700/50 pt-2 mt-2 space-y-2">
                  <p className="text-zinc-500 text-xs font-medium">Opções avançadas:</p>
                  
                  {/* End by time before event */}
                  <div className="bg-zinc-900/50 p-2 rounded">
                    <Label className="text-zinc-500 text-xs mb-1 block">Fim da venda (tempo antes do evento)</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input 
                        type="number" 
                        min="0"
                        value={lot.end_before_value || ""} 
                        onChange={(e) => updateLot(idx, "end_before_value", e.target.value ? parseInt(e.target.value) : null)} 
                        className="bg-zinc-800 border-zinc-700 text-white h-7 text-xs w-16" 
                        placeholder="Nº" 
                      />
                      <select 
                        value={lot.end_before_unit || ""} 
                        onChange={(e) => updateLot(idx, "end_before_unit", e.target.value)}
                        className="bg-zinc-800 border border-zinc-700 text-white rounded px-2 h-7 text-xs"
                      >
                        <option value="">Unidade</option>
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                        <option value="days">Dias</option>
                      </select>
                      <select 
                        value={lot.end_before_reference || ""} 
                        onChange={(e) => updateLot(idx, "end_before_reference", e.target.value)}
                        className="bg-zinc-800 border border-zinc-700 text-white rounded px-2 h-7 text-xs"
                      >
                        <option value="">Antes de...</option>
                        <option value="event_start">Início do evento</option>
                        <option value="event_end">Fim do evento</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Start after another lot sells out */}
                  {idx > 0 && (
                    <div className="bg-zinc-900/50 p-2 rounded">
                      <Label className="text-zinc-500 text-xs mb-1 block">Virar lote quando esgotar</Label>
                      <select 
                        value={lot.start_after_lot || ""} 
                        onChange={(e) => updateLot(idx, "start_after_lot", e.target.value)}
                        className="bg-zinc-800 border border-zinc-700 text-white rounded px-2 h-7 text-xs w-full"
                      >
                        <option value="">Nenhum (usar data de início)</option>
                        {form.lots
                          .filter((_, i) => i < idx)
                          .map((l, i) => (
                            <option key={i} value={l.name}>Quando "{l.name}" esgotar</option>
                          ))
                        }
                      </select>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save for future events toggle (only in embedded mode) */}
      {embedded && !editingItem && (
        <div className="border-t border-zinc-800 pt-4">
          <label className="flex items-center gap-3 cursor-pointer group">
            <button
              type="button"
              onClick={() => setSaveForFuture(!saveForFuture)}
              className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 ${saveForFuture ? "bg-[#fccf1f]" : "bg-zinc-700"}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${saveForFuture ? "translate-x-4" : ""}`} />
            </button>
            <span className="text-zinc-300 text-sm group-hover:text-white transition-colors">
              Salvar ingresso para eventos futuros
            </span>
          </label>
          <p className="text-zinc-500 text-xs mt-1 ml-[52px]">
            {saveForFuture 
              ? "O ingresso ficará disponível no catálogo para reutilizar em outros eventos."
              : "O ingresso será usado apenas neste evento."}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
        <Button type="button" variant="outline" onClick={() => embedded && onClose ? onClose() : setShowModal(false)} className="border-zinc-700">
          Cancelar
        </Button>
        <Button 
          type="button"
          onClick={handleSave} 
          className="bg-[#fccf1f] text-black hover:bg-[#e6bc1c]"
          data-testid="save-ticket-type-btn"
        >
          <Check className="w-4 h-4 mr-2" />
          {editingItem ? "Salvar Alterações" : saveForFuture || !embedded ? "Criar Tipo de Ingresso" : "Adicionar ao Evento"}
        </Button>
      </div>
    </div>
  );

  // Se em modo embedded, renderiza apenas o formulário
  if (embedded) {
    return renderForm();
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Ticket className="w-6 h-6 text-[#fccf1f]" />
            Catálogo de Ingressos
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Crie tipos de ingresso reutilizáveis com lotes e preços configuráveis
          </p>
        </div>
        {!readOnly && (
          <Button 
            onClick={handleOpenCreate} 
            className="bg-[#fccf1f] text-black hover:bg-[#e6bc1c]"
            data-testid="create-ticket-type-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Tipo
          </Button>
        )}
      </div>

      {!readOnly && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <DollarSign className="w-5 h-5 text-[#fccf1f]" />
              Taxa Padrão dos Ingressos
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-zinc-400">Taxa %</Label>
              <Input
                type="number"
                step="0.1"
                value={globalFees.percentage}
                onChange={(e) => setGlobalFees((prev) => ({ ...prev, percentage: parseFloat(e.target.value) || 0 }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Taxa Fixa (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={globalFees.fixed}
                onChange={(e) => setGlobalFees((prev) => ({ ...prev, fixed: parseFloat(e.target.value) || 0 }))}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={saveGlobalFeeSettings}
                disabled={savingGlobalFees}
                className="w-full bg-[#fccf1f] text-black hover:bg-[#e6bc1c]"
              >
                {savingGlobalFees ? "Salvando..." : "Salvar Taxa Padrão"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket Types List */}
      <div className="space-y-3">
        {ticketTypes.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-10 text-center">
              <Ticket className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">Nenhum tipo de ingresso cadastrado</p>
              {!readOnly && (
                <Button onClick={handleOpenCreate} className="mt-4 bg-[#fccf1f] text-black">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Tipo
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
paged(ticketTypes, (tt) => (
            <Card 
              key={tt.id} 
              className={`bg-zinc-900/50 border-zinc-800 ${!tt.is_active ? "opacity-50" : ""}`}
              data-testid={`ticket-type-${tt.id}`}
            >
              <CardHeader className="py-4 cursor-pointer" onClick={() => setExpandedId(expandedId === tt.id ? null : tt.id)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-[#fccf1f]/10 rounded-lg">
                      <Ticket className="w-5 h-5 text-[#fccf1f]" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg flex items-center gap-2">
                        {tt.name}
                        {!tt.is_active && (
                          <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-0.5 rounded">Inativo</span>
                        )}
                      </CardTitle>
                      <p className="text-zinc-400 text-sm">
                        {tt.active_lot ? (
                          <span className="text-green-400">
                            {tt.active_lot.name}: {formatCurrency(tt.current_price)}
                          </span>
                        ) : (
                          <span>Base: {formatCurrency(tt.base_price)}</span>
                        )}
                        {tt.lots?.length > 0 && (
                          <span className="text-zinc-500 ml-2">• {tt.lots.length} lote(s)</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!readOnly && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDuplicate(tt); }}
                          className="border-zinc-700 text-zinc-400 hover:text-white"
                          data-testid={`duplicate-${tt.id}`}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleOpenEdit(tt); }}
                          className="border-zinc-700 text-zinc-400 hover:text-white"
                          data-testid={`edit-${tt.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDelete(tt.id); }}
                          className="border-zinc-700 text-red-400 hover:text-red-300 hover:border-red-700"
                          data-testid={`delete-${tt.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {expandedId === tt.id ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                  </div>
                </div>
              </CardHeader>
              
              {expandedId === tt.id && (
                <CardContent className="border-t border-zinc-800 pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-zinc-800/50 p-3 rounded">
                      <p className="text-zinc-500 text-xs">Preço Base</p>
                      <p className="text-white font-medium">{formatCurrency(tt.base_price)}</p>
                    </div>
	                    <div className="bg-zinc-800/50 p-3 rounded">
	                      <p className="text-zinc-500 text-xs">Taxa</p>
	                      <p className="text-white font-medium">
	                        {tt.use_custom_fee
	                          ? `${tt.fee_percentage}% + ${formatCurrency(tt.fee_fixed)}`
	                          : `Padrão global: ${globalFees.percentage}% + ${formatCurrency(globalFees.fixed)}`}
	                      </p>
	                    </div>
                    <div className="bg-zinc-800/50 p-3 rounded">
                      <p className="text-zinc-500 text-xs">Qtd. por compra</p>
                      <p className="text-white font-medium">{tt.min_qty} - {tt.max_qty || "∞"}</p>
                    </div>
                    <div className="bg-zinc-800/50 p-3 rounded">
                      <p className="text-zinc-500 text-xs">Capacidade</p>
                      <p className="text-white font-medium">{tt.capacity || "Ilimitada"}</p>
                    </div>
                    <div className="bg-zinc-800/50 p-3 rounded">
                      <p className="text-zinc-500 text-xs">Pagamento</p>
                      <p className="text-white font-medium">
                        {tt.payment_provider === "asaas" ? "Asaas" : tt.payment_provider === "infinitepay" ? "InfinitePay" : "Automático"}
                      </p>
                    </div>
                  </div>

                  {tt.quantity_pricing?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-zinc-400 text-sm font-medium mb-2 flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Desconto Progressivo por Quantidade
                      </p>
                      <div className="grid gap-2 md:grid-cols-2">
                        {tt.quantity_pricing.map((rule, idx) => (
                          <div key={`${tt.id}-quantity-rule-${idx}`} className="bg-zinc-800/50 p-3 rounded">
                            <p className="text-white font-medium">{describeQuantityPricingRule(rule)}</p>
                            <p className="text-zinc-500 text-xs mt-1">
                              {rule.label || "A melhor faixa válida é aplicada automaticamente conforme a quantidade."}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Lots */}
                  {tt.lots?.length > 0 && (
                    <div>
                      <p className="text-zinc-400 text-sm font-medium mb-2 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Lotes ({tt.lots.length})
                      </p>
                      <div className="space-y-2">
                        {tt.lots.map((lot, idx) => (
                          <div 
                            key={lot.id || idx} 
                            className={`bg-zinc-800/50 p-3 rounded flex items-center justify-between ${
                              tt.active_lot?.id === lot.id ? "border border-green-600/50" : ""
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <span className="text-zinc-500 text-sm w-8">{idx + 1}.</span>
                              <div>
                                <p className="text-white font-medium flex items-center gap-2">
                                  {lot.name}
                                  {tt.active_lot?.id === lot.id && (
                                    <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded">ATIVO</span>
                                  )}
                                </p>
                                <p className="text-zinc-400 text-sm">
                                  {formatCurrency(lot.price)}
                                  {lot.quantity_limit && ` • Limite: ${lot.quantity_limit}`}
                                </p>
                              </div>
                            </div>
                            <div className="text-right text-sm text-zinc-500">
                              {lot.starts_at && <p>Início: {formatDateTime(lot.starts_at)}</p>}
                              {lot.ends_at && <p>Fim: {formatDateTime(lot.ends_at)}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#fccf1f]" />
              {editingItem ? "Editar Tipo de Ingresso" : "Novo Tipo de Ingresso"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Nome *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ex: Ingresso Individual"
                  data-testid="input-name"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Descrição</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Descrição opcional"
                />
              </div>
            </div>

            {/* Pricing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-400">Preço Base (R$)</Label>
          <Input
            type="number"
            step="0.01"
            value={form.base_price}
            onChange={(e) => setForm({ ...form, base_price: parseFloat(e.target.value) || 0 })}
            className="bg-zinc-800 border-zinc-700 text-white"
            data-testid="input-price"
          />
        </div>
        <div className="md:col-span-2 space-y-3 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              onClick={() => setForm((prev) => ({
                ...prev,
                use_custom_fee: !prev.use_custom_fee,
                fee_percentage: !prev.use_custom_fee ? prev.fee_percentage : globalFees.percentage,
                fee_fixed: !prev.use_custom_fee ? prev.fee_fixed : globalFees.fixed,
              }))}
              className={`w-10 h-6 rounded-full transition-colors ${form.use_custom_fee ? "bg-[#fccf1f]" : "bg-zinc-700"}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${form.use_custom_fee ? "translate-x-4" : ""}`} />
            </button>
            <div>
              <p className="text-zinc-200 text-sm font-medium">Taxa Personalizada</p>
              <p className="text-zinc-500 text-xs">Desmarcado: usa a taxa padrão da Aba Ingressos</p>
            </div>
          </label>
          {form.use_custom_fee ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Taxa (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.fee_percentage}
                  onChange={(e) => setForm({ ...form, fee_percentage: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Taxa Fixa (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.fee_fixed}
                  onChange={(e) => setForm({ ...form, fee_fixed: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-300">
              Taxa padrão ativa: <span className="font-medium text-white">{globalFees.percentage}% + {formatCurrency(globalFees.fixed)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <Label className="text-zinc-300">Provedor de pagamento preferencial</Label>
        <select
          value={form.payment_provider}
          onChange={(e) => setForm({ ...form, payment_provider: e.target.value })}
          className="mt-2 h-10 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 text-sm text-white"
          data-testid="ticket-payment-provider-modal"
        >
          <option value="auto">Automático: Asaas no presencial e InfinitePay no digital</option>
          <option value="infinitepay">InfinitePay</option>
          <option value="asaas">Asaas (mínimo de R$ 5,00 no total final)</option>
        </select>
        <p className="mt-2 text-xs text-zinc-500">
          Se descontos reduzirem uma cobrança Asaas para menos de R$ 5,00, ela será direcionada automaticamente ao InfinitePay e registrada para auditoria.
        </p>
      </div>

      {/* Quantity & Capacity */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400">Qtd. Mínima</Label>
                <Input
                  type="number"
                  value={form.min_qty}
                  onChange={(e) => setForm({ ...form, min_qty: parseInt(e.target.value) || 1 })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Qtd. Máxima</Label>
                <Input
                  type="number"
                  value={form.max_qty || ""}
                  onChange={(e) => setForm({ ...form, max_qty: e.target.value ? parseInt(e.target.value) : null })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ilimitado"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Capacidade Total</Label>
                <Input
                  type="number"
                  value={form.capacity || ""}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value ? parseInt(e.target.value) : null })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Ilimitada"
                />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setForm({ ...form, is_active: !form.is_active })}
                className={`w-10 h-6 rounded-full transition-colors ${form.is_active ? "bg-green-600" : "bg-zinc-700"}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${form.is_active ? "translate-x-4" : ""}`} />
              </button>
              <span className="text-zinc-400 text-sm">Ativo</span>
            </div>

            {/* Visibility */}
            <div>
              <Label className="text-zinc-400 mb-2 block">Visibilidade do Ingresso</Label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: "available", label: "Visível quando disponível", icon: "👁️" },
                  { value: "always", label: "Sempre visível", icon: "✨" },
                  { value: "hidden", label: "Oculto", icon: "🔒" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, visibility: opt.value })}
                    className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      form.visibility === opt.value
                        ? "bg-[#fccf1f] text-black"
                        : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                    }`}
                  >
                    <span>{opt.icon}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
              {form.visibility === "hidden" && (
                <p className="text-amber-400/80 text-xs mt-2 bg-amber-500/10 p-2 rounded">
                  ⚠️ Ingressos ocultos só ficam visíveis quando um cupom específico é aplicado.
                </p>
              )}
            </div>

            {/* Quantity Pricing Section */}
            <div className="border-t border-zinc-800 pt-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Desconto Progressivo por Quantidade ({form.quantity_pricing.length})
                </Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addQuantityPricingRule}
                  className="border-zinc-700 text-zinc-400"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Adicionar Faixa
                </Button>
              </div>

              {form.quantity_pricing.length === 0 ? (
                <p className="text-zinc-500 text-sm bg-zinc-800/50 p-4 rounded text-center">
                  Sem desconto progressivo configurado. O lote vigente define o preço base e as faixas entram por quantidade.
                </p>
              ) : (
                <div className="space-y-3 mb-4">
                  <div className="rounded-xl border border-purple-500/20 bg-purple-500/10 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                      <div>
                        <p className="text-white font-semibold">Simulador de Oferta</p>
                        <p className="text-zinc-400 text-sm">
                          Veja exatamente qual faixa será aplicada para a quantidade escolhida.
                        </p>
                      </div>
                      <div className="w-full md:w-40">
                        <Label className="text-zinc-400 text-xs">Quantidade simulada</Label>
                        <Input
                          type="number"
                          min="1"
                          value={quantityPreview}
                          onChange={(e) => setQuantityPreview(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className="bg-zinc-900 border-zinc-700 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-4 mt-4">
                      <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                        <p className="text-zinc-500 text-xs">Preço base por ingresso</p>
                        <p className="text-white font-semibold">{formatCurrency(quantityPricingPreview.baseUnitPrice)}</p>
                      </div>
                      <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                        <p className="text-zinc-500 text-xs">Preço aplicado</p>
                        <p className="text-white font-semibold">{formatCurrency(quantityPricingPreview.unitPrice)}</p>
                      </div>
                      <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                        <p className="text-zinc-500 text-xs">Economia nesta quantidade</p>
                        <p className="text-emerald-400 font-semibold">{formatCurrency(quantityPricingPreview.savings)}</p>
                      </div>
                      <div className="rounded-lg bg-zinc-900/60 border border-white/5 p-3">
                        <p className="text-zinc-500 text-xs">Faixa aplicada</p>
                        <p className="text-white font-semibold text-sm">
                          {quantityPricingPreview.appliedRule
                            ? (quantityPricingPreview.appliedRule.label || describeQuantityPricingRule(quantityPricingPreview.appliedRule))
                            : "Preço base do lote"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {form.quantity_pricing.map((rule, idx) => (
                    <div key={`quantity-rule-dialog-${idx}`} className="bg-zinc-800/50 p-4 rounded-xl space-y-3 border border-white/5">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-white font-medium">{describeQuantityPricingRule(rule)}</p>
                            {idx === form.quantity_pricing.length - 1 && (
                              <span className="text-[10px] uppercase tracking-wide bg-[#fccf1f]/20 text-[#fccf1f] px-2 py-1 rounded-full">
                                Melhor oferta
                              </span>
                            )}
                          </div>
                          <p className="text-zinc-500 text-xs">
                            A melhor faixa válida será aplicada sobre o lote vigente no checkout.
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuantityPricingRule(idx)}
                          className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-zinc-500 text-xs">Qtd. Mínima</Label>
                          <Input
                            type="number"
                            min="1"
                            value={rule.min_quantity}
                            onChange={(e) => updateQuantityPricingRule(idx, "min_quantity", parseInt(e.target.value, 10) || 1)}
                            className="bg-zinc-900 border-zinc-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Tipo da Regra</Label>
                          <select
                            value={rule.rule_type}
                            onChange={(e) => updateQuantityPricingRule(idx, "rule_type", e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-md px-3 h-10"
                          >
                            <option value="percent_discount">% OFF sobre o lote</option>
                            <option value="fixed_discount">Desconto fixo (R$)</option>
                            <option value="fixed_price">Preço final fixo (legado)</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">
                            {rule.rule_type === "fixed_price"
                              ? "Preço Final (R$)"
                              : rule.rule_type === "fixed_discount"
                                ? "Desconto (R$)"
                                : "Desconto (%)"}
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            step={rule.rule_type === "percent_discount" ? "0.1" : "0.01"}
                            value={rule.value}
                            onChange={(e) => updateQuantityPricingRule(idx, "value", parseFloat(e.target.value) || 0)}
                            className="bg-zinc-900 border-zinc-700 text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Rótulo Opcional</Label>
                          <Input
                            value={rule.label || ""}
                            onChange={(e) => updateQuantityPricingRule(idx, "label", e.target.value)}
                            className="bg-zinc-900 border-zinc-700 text-white"
                            placeholder="Ex: Leve 3 e pague menos"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lots Section */}
            <div className="border-t border-zinc-800 pt-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="text-zinc-400 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Lotes ({form.lots.length})
                </Label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addLot}
                  className="border-zinc-700 text-zinc-400"
                  data-testid="add-lot-btn"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Adicionar Lote
                </Button>
              </div>

              {form.lots.length === 0 ? (
                <p className="text-zinc-500 text-sm bg-zinc-800/50 p-4 rounded text-center">
                  Sem lotes configurados. O preço base será usado.
                </p>
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {form.lots.map((lot, idx) => (
                    <div key={lot.id || idx} className="bg-zinc-800/50 p-3 rounded space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col gap-1">
                            <button 
                              onClick={() => moveLot(idx, -1)} 
                              disabled={idx === 0}
                              className="text-zinc-500 hover:text-white disabled:opacity-30"
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button 
                              onClick={() => moveLot(idx, 1)} 
                              disabled={idx === form.lots.length - 1}
                              className="text-zinc-500 hover:text-white disabled:opacity-30"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="text-zinc-400 text-sm font-medium">Lote {idx + 1}</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeLot(idx)}
                          className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {describeLotConditions(lot) && (
                        <p className="text-[11px] text-zinc-400 rounded-md bg-zinc-900/60 border border-zinc-700/70 px-2.5 py-2">
                          {describeLotConditions(lot)}
                        </p>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-zinc-500 text-xs">Nome</Label>
                          <Input
                            value={lot.name}
                            onChange={(e) => updateLot(idx, "name", e.target.value)}
                            className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                            placeholder="1º Lote"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Preço (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={lot.price}
                            onChange={(e) => updateLot(idx, "price", parseFloat(e.target.value) || 0)}
                            className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Início (Data)</Label>
                          <Input
                            type="datetime-local"
                            value={lot.starts_at ? lot.starts_at.slice(0, 16) : ""}
                            onChange={(e) => updateLot(idx, "starts_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                            className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Fim (Data)</Label>
                          <Input
                            type="datetime-local"
                            value={lot.ends_at ? lot.ends_at.slice(0, 16) : ""}
                            onChange={(e) => updateLot(idx, "ends_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                            className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-xs">Limite de Quantidade</Label>
                          <Input
                            type="number"
                            value={lot.quantity_limit || ""}
                            onChange={(e) => updateLot(idx, "quantity_limit", e.target.value ? parseInt(e.target.value) : null)}
                            className="bg-zinc-900 border-zinc-700 text-white text-sm h-8"
                            placeholder="Ilimitado"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => updateLot(idx, "is_active", !lot.is_active)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                              lot.is_active ? "bg-green-600/20 text-green-400" : "bg-zinc-700 text-zinc-400"
                            }`}
                          >
                            {lot.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            {lot.is_active ? "Ativo" : "Inativo"}
                          </button>
                        </div>
                      </div>
                      
                      {/* Advanced Options */}
                      <div className="border-t border-zinc-700/50 pt-2 mt-2 space-y-2">
                        <p className="text-zinc-500 text-xs font-medium">Opções avançadas:</p>
                        
                        {/* End by time before event */}
                        <div className="bg-zinc-900/50 p-2 rounded">
                          <Label className="text-zinc-500 text-xs mb-1 block">Fim da venda (tempo antes do evento)</Label>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Input 
                              type="number" 
                              min="0"
                              value={lot.end_before_value || ""} 
                              onChange={(e) => updateLot(idx, "end_before_value", e.target.value ? parseInt(e.target.value) : null)} 
                              className="bg-zinc-800 border-zinc-700 text-white h-7 text-xs w-16" 
                              placeholder="Nº" 
                            />
                            <select 
                              value={lot.end_before_unit || ""} 
                              onChange={(e) => updateLot(idx, "end_before_unit", e.target.value)}
                              className="bg-zinc-800 border border-zinc-700 text-white rounded px-2 h-7 text-xs"
                            >
                              <option value="">Unidade</option>
                              <option value="minutes">Minutos</option>
                              <option value="hours">Horas</option>
                              <option value="days">Dias</option>
                            </select>
                            <select 
                              value={lot.end_before_reference || ""} 
                              onChange={(e) => updateLot(idx, "end_before_reference", e.target.value)}
                              className="bg-zinc-800 border border-zinc-700 text-white rounded px-2 h-7 text-xs"
                            >
                              <option value="">Antes de...</option>
                              <option value="event_start">Início do evento</option>
                              <option value="event_end">Fim do evento</option>
                            </select>
                          </div>
                        </div>
                        
                        {/* Start after another lot sells out */}
                        {idx > 0 && (
                          <div className="bg-zinc-900/50 p-2 rounded">
                            <Label className="text-zinc-500 text-xs mb-1 block">Virar lote quando esgotar</Label>
                            <select 
                              value={lot.start_after_lot || ""} 
                              onChange={(e) => updateLot(idx, "start_after_lot", e.target.value)}
                              className="bg-zinc-800 border border-zinc-700 text-white rounded px-2 h-7 text-xs w-full"
                            >
                              <option value="">Nenhum (usar data de início)</option>
                              {form.lots
                                .filter((_, i) => i < idx)
                                .map((l, i) => (
                                  <option key={i} value={l.name}>Quando "{l.name}" esgotar</option>
                                ))
                              }
                            </select>
                          </div>
                        )}
                        
                        {/* Lot Visibility */}
                        <div className="bg-zinc-900/50 p-2 rounded">
                          <Label className="text-zinc-500 text-xs mb-1 block">Visibilidade do Lote</Label>
                          <div className="flex gap-1 flex-wrap">
                            {[
                              { value: "available", label: "Disponível" },
                              { value: "always", label: "Sempre" },
                              { value: "hidden", label: "Oculto 🔒" },
                            ].map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => updateLot(idx, "visibility", opt.value)}
                                className={`px-2 py-1 rounded text-xs transition-colors ${
                                  (lot.visibility || "available") === opt.value
                                    ? "bg-[#fccf1f] text-black"
                                    : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                className="bg-[#fccf1f] text-black hover:bg-[#e6bc1c]"
                data-testid="save-ticket-type-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {editingItem ? "Salvar Alterações" : "Criar Tipo de Ingresso"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
